import React, {useEffect} from 'react';
import {cn} from '@/helpers/shadcn/utils';
import {cva} from 'class-variance-authority';
import {Icon} from '@/components/atoms';
import {ALERT_TYPES} from '@/utils/constants/commonConstants';
import PropTypes from 'prop-types';

/**
 * Alert component to display different types of alert messages.
 *
 * @param {Object} props - The properties object.
 * @param {'success' | 'error' | 'info' | 'warning'} props.type - The type of alert to display.
 * @param {string} props.message - The message to display in the alert.
 * @param {boolean} [props.show=false] - Whether the alert is visible or not.
 * @param {Function} props.onCloseClick - The function to call when the close button is clicked.
 *
 * @returns {JSX.Element|null} The Alert component or null if not visible.
 */
const Alert = ({type, message, show = false, onCloseClick}) => {
  useEffect(() => {
    const itv = setTimeout(() => onCloseClick?.(), 5000);
    return () => clearTimeout(itv);
  }, [message, onCloseClick, show, type]);

  // z-index must clear the @pepsico-ds/ui modal layers (overlay 2222, modal
  // 3333) so toasts raised from inside a modal are not hidden behind it.
  const alertVariants = cva(
    `absolute right-4 top-6 z-[4000] flex w-full rounded-md px-4 py-2 alert max-w-[calc(100vw-7.5rem)]`,
    {
      variants: {
        type: {
          success: 'bg-support-success-primary',
          error: 'bg-support-error-primary',
          info: 'bg-support-info-primary',
          warning: 'bg-support-warning-primary'
        }
      }
    }
  );

  if (!show) {
    return null;
  }

  return (
    <div
      data-testid={`alert-${type}`}
      className={cn(alertVariants({type}))}
      role='alert'>
      <div className='inline-flex h-6 w-6 items-center justify-center rounded-lg'>
        {type === ALERT_TYPES.Success && (
          <Icon
            icon='check_circle_outline'
            size='medium'
            className='text-button-icon'
          />
        )}
        {type === ALERT_TYPES.Error && (
          <Icon icon='error' size='medium' className='text-button-icon' />
        )}
        {type === ALERT_TYPES.Info && (
          <Icon
            icon='check_circle_outline'
            size='medium'
            className='text-button-icon'
          />
        )}
        {type === ALERT_TYPES.Warning && (
          <Icon
            icon='warning_amber'
            size='medium'
            className='text-text-primary'
          />
        )}
      </div>
      <div className='text-md ml-3 flex w-full items-center'>
        {message && (
          <div
            className={`text-sm ${type === ALERT_TYPES.Warning ? 'text-black' : 'text-white'}`}>
            {message}
          </div>
        )}
      </div>
      <button
        type='button'
        className='mt-1 inline-flex h-4 w-4 items-center justify-center text-icon'
        data-dismiss-target={`#alert-${type}`}
        data-testid='alert-close-btn'
        aria-label='Close'
        onClick={() => onCloseClick?.()}>
        <span className='sr-only'>Close</span>
        <Icon
          icon='close'
          size='small'
          className={
            type === ALERT_TYPES.Warning
              ? 'text-text-primary'
              : 'text-button-icon'
          }
        />
      </button>
    </div>
  );
};

Alert.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']).isRequired,
  message: PropTypes.string.isRequired,
  show: PropTypes.bool,
  onCloseClick: PropTypes.func.isRequired
};

export default React.memo(Alert);
